// ============================================
// useOfflineQueue — UKA-X01 D9
//
// React hook for offline verdict queue management.
//
// Subscribes to:
//   - window 'online' / 'offline' events
//   - custom 'hlx-queue-updated' event (dispatched by useVerdictSubmit on enqueue)
//
// Triggers automatic drain on 'online'.
// Drain writes each queued verdict to Firestore (idempotent — skip if exists).
// ============================================

import { useCallback, useEffect, useState } from 'react';
import {
  doc,
  getDoc,
  setDoc,
} from 'firebase/firestore';
import { db } from '../../services/firebase';
import { getQueueDepth, drainQueue } from '../lib/offlineQueue';
import type { QueuedVerdict } from '../lib/offlineQueue';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface UseOfflineQueueResult {
  queueDepth: number;
  isOnline: boolean;
  isDraining: boolean;
  drain: () => void;
}

// ── Drain handler ─────────────────────────────────────────────────────────────

/**
 * Processes a single queued verdict against Firestore.
 * Idempotent: if the verdict doc already exists, returns 'skip'.
 * Writes via setDoc with merge:false (verdict is immutable once written).
 */
async function processQueuedVerdict(
  item: QueuedVerdict,
): Promise<'success' | 'skip' | 'error'> {
  const verdictRef = doc(
    db,
    'tenants',
    item.tenantId,
    'content',
    item.contentId,
    'verdicts',
    item.verdictId,
  );

  try {
    // Idempotency check — skip if verdict already written
    const existing = await getDoc(verdictRef);
    if (existing.exists()) {
      return 'skip';
    }

    await setDoc(verdictRef, item.verdictData);
    return 'success';
  } catch {
    return 'error';
  }
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useOfflineQueue(): UseOfflineQueueResult {
  const [queueDepth, setQueueDepth] = useState(0);
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );
  const [isDraining, setIsDraining] = useState(false);

  // Refresh queue depth from IDB
  const refreshDepth = useCallback(async () => {
    try {
      const depth = await getQueueDepth();
      setQueueDepth(depth);
    } catch {
      // IDB unavailable — leave stale count rather than crash
    }
  }, []);

  // Drain all queued verdicts to Firestore
  const drain = useCallback(async () => {
    if (isDraining) return;
    setIsDraining(true);

    try {
      await drainQueue(processQueuedVerdict);
    } catch {
      // drainQueue is internally resilient; top-level catch is a safety net
    } finally {
      setIsDraining(false);
      await refreshDepth();
    }
  }, [isDraining, refreshDepth]);

  // Expose synchronous drain trigger for OfflineIndicator "Sync Now" button
  const drainSync = useCallback(() => {
    void drain();
  }, [drain]);

  // ── Effects ──────────────────────────────────────────────────────────────

  // Read initial queue depth on mount
  useEffect(() => {
    void refreshDepth();
  }, [refreshDepth]);

  // Listen for online/offline events
  useEffect(() => {
    function handleOnline() {
      setIsOnline(true);
      void drain();
    }

    function handleOffline() {
      setIsOnline(false);
    }

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [drain]);

  // Listen for custom queue-updated event (dispatched by useVerdictSubmit on enqueue)
  useEffect(() => {
    function handleQueueUpdated() {
      void refreshDepth();
    }

    window.addEventListener('hlx-queue-updated', handleQueueUpdated);
    return () => {
      window.removeEventListener('hlx-queue-updated', handleQueueUpdated);
    };
  }, [refreshDepth]);

  return {
    queueDepth,
    isOnline,
    isDraining,
    drain: drainSync,
  };
}
